import React, { FC } from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import { Paper } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import { Title, VirtualizedList, TextField, StatusField, GlTypeIcon } from '~/components';
import { Column } from '~/components/VirtualizedList';
import {
  selectors,
  ResourceListType,
  getVirtualizedListBegin,
  getVirtualizedListSuccess,
  getVirtualizedListFail,
} from '~/ducks/virtualizedList';
import { Account, RootState } from '~/types';
import { searchAccounts } from '~/api/accounts';
import { time } from '~/utils';
import ReferenceField from '~/components/ReferenceField';
import { NoPermissions } from '~/components/ra';
import VirtualizedListWrapper from '~/components/VirtualizedListWrapper';
import Filters from './components/list/ListFilters';
import palette from '~/theme/palette';

export const resource = 'accounts';

const AccountsList: FC<any> = (props) => {
  const { history } = props;
  const { permissions } = usePermissions();
  const dispatch = useDispatch();
  const { data, hasNextPage, isNextPageLoading } = useSelector<
    RootState,
    ResourceListType<Account>
  >(selectors.getList(resource));
  const filters = useSelector(selectors.getFilters(resource));
  const { cursors } = useSelector(selectors.getList(resource));

  const translate = useTranslate();
  const t = (key: string) => translate(`resources.accounts.fields.${key}`);

  const loadNextPage = async () => {
    if (isNextPageLoading || (!cursors.next && data.length > 0)) return;
    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource, queryToken }));
      const { data: resData } = await searchAccounts({
        next: cursors.next || undefined,
        limit: 100,
        ...filters,
      });
      dispatch(
        getVirtualizedListSuccess({
          resource,
          responseData: { cursors: resData.cursors, records: resData.accounts },
          queryToken,
        })
      );
    } catch (error) {
      const err = error as Error;
      dispatch(getVirtualizedListFail({ resource, error: err, queryToken }));
    }
  };

  const columns: Column<Account>[] = [
    {
      selectCellData: (rowData) => rowData,
      cellDataWrapperComponent: NumberCell,
      id: 'number',
      header: t('number'),
      style: { flexBasis: 180, minWidth: 130 },
    },
    {
      selectCellData: (rowData) => rowData.type,
      id: 'type',
      header: t('type'),
      style: { flexBasis: 120, minWidth: 80 },
    },
    {
      selectCellData: (rowData) => rowData.glType,
      cellDataWrapperComponent: GlTypeCell,
      id: 'glType',
      header: t('glType'),
      style: { flexBasis: 70, minWidth: 60 },
    },
    {
      selectCellData: (rowData) => rowData.alias,
      id: 'alias',
      header: t('alias'),
      style: { flexBasis: 200, minWidth: 100 },
    },
    {
      selectCellData: (rowData) => rowData.currency,
      id: 'currency',
      header: t('currency'),
      style: { flexBasis: 80, minWidth: 60 },
    },
    {
      selectCellData: (rowData) => rowData,
      cellDataWrapperComponent: OwnerCell,
      id: 'owner',
      header: t('owner'),
      style: { flexBasis: 250, minWidth: 150 },
    },
    {
      selectCellData: (rowData) => rowData.created,
      cellDataWrapperComponent: CreatedCell,
      id: 'createdAt',
      header: t('createdAt'),
      style: { flexBasis: 150, minWidth: 100 },
    },
    {
      selectCellData: (rowData) => rowData.status,
      cellDataWrapperComponent: StatusCell,
      id: 'status',
      header: t('status'),
      style: { flexBasis: 110, minWidth: 80 },
    },
  ];

  if (!permissions) return null;
  if (!permissions.includes('account.list')) return <NoPermissions />;

  return (
    <VirtualizedListWrapper
      title={<Title resource={resource} />}
      filters={<Filters cleanFilters={history.action !== 'POP'} />}
    >
      <Paper style={{ flex: '1 1 auto', overflow: 'hidden', ...fixBottomShadow }}>
        <VirtualizedList<Account>
          resource={resource}
          columns={columns}
          data={data}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          filters={filters}
          outterTable
          rowHeight={48}
          getRowLink={(rowData) =>
            permissions?.includes('account.view') ? `/accounts/${rowData.id}/show` : undefined
          }
          minHeight={0}
        />
      </Paper>
    </VirtualizedListWrapper>
  );
};

// Cell components

const NumberCell = ({ children, style }: { children: Account; style?: any }) => {
  const errorColor = children?.error ? { color: palette.error.main } : undefined;
  return <span style={{ ...style, ...errorColor }}>{children?.number}</span>;
};

const OwnerCell = ({ children }: { children: Account; style?: any }) => {
  if (!children?.profileId) return null;
  return (
    <ReferenceField
      record={{ id: children.profileId, type: children.profileType }}
      withLabel={false}
    />
  );
};

const CreatedCell = ({ children, style }: { children: string; style?: any }) => {
  if (!children) return null;
  const created = time(children);
  return (
    <TextField compact label={created.format('LL')} style={style}>
      {created.format('LT')}
    </TextField>
  );
};

const StatusCell = ({ children, style }: { children: Account['status']; style?: any }) => {
  return <StatusField status={children} style={style} />;
};

const GlTypeCell = ({ children, style }: { children: Account['glType']; style?: any }) => {
  return <GlTypeIcon code={children} style={style} />;
};

const fixBottomShadow = { paddingBottom: 2 };

export default AccountsList;
